import { useDispatch, useSelector } from "react-redux";
import PendingTaskHeader from "../../../components/pending-task-header/PendingTaskHeader";
import bmrSubjectApi from "../../services/bmrSubjectService";
import { useEffect, useState, useCallback, useRef } from "react";
import { PendingTasks } from "../../utils/formik/initialValues";
import bmrEditorApi from "../../services/bmrEditorManagementService";
import TaskNotificationTile from "../../components/taskNotificationTile/TaskNotificationTile";
import PendingTaskTile from "../../../components/pending-task-tile/PendingTaskTile";
import { BackNavIcon } from "../../../assets/common-svg/CommonSvg";
import { useNavigate } from "react-router";
import { notify } from "../../utils/methods/notification";

const PendingTask = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const role_id = useSelector((state) => state.loginData.role_id);
  const login_id = useSelector((state) => state.loginData.loginId);
  const bmr_modules = useSelector((state) => state.loginData).bmr_modules;
  const singleModuleAccess = bmr_modules.length == 1 ? true : false;
  const role_slug = useSelector((state) => state.loginData.role_slug);

  const [taskCount, setTaskCount] = useState({});
  const [search, setSearch] = useState("");
  const [isClosed, setIsClosed] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);
  const [taskData, setTaskData] = useState([]);
  const [requestData, setRequestData] = useState([]);
  const [phaseData, setPhaseData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [maxPages, setMaxPages] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [alignment, setAlignment] = useState(
    role_id === 1 ? `Pending CR` : `Pending Tasks`
  );
  const [filters, setFilters] = useState({});

  // Ref to track if we're currently loading more data
  const isLoadingMore = useRef(false);
  const abortControllerRef = useRef(null);

  // Helper function to validate search input
  const isValidSearchInput = (searchValue) => {
    if (!searchValue || searchValue.trim() === "") {
      return searchValue === "";
    }
    if (searchValue.endsWith(" ") && searchValue.trim().length > 0) {
      return false;
    }
    return true;
  };

  // Helper function to get clean search value for API
  const getCleanSearchValue = (searchValue) => {
    return searchValue ? searchValue.trim() : "";
  };

  // FIXED: Improved API call function with proper state management
  const handleGetHistoryData = useCallback(
    async (
      status = null,
      optionalfilter = {},
      isLoadMore = false,
      targetPage = 1
    ) => {
      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Prevent duplicate calls
      if (isLoadingMore.current && isLoadMore) return;

      // Validate search input before making API call
      if (!isValidSearchInput(search)) {
        return;
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      if (isLoadMore) {
        isLoadingMore.current = true;
      } else {
        setLoading(true);
        // Reset pagination for new searches/filters
        setTaskData([]);
        setHasMoreData(true);
      }

      // Convert status to integer if it's provided, otherwise determine from alignment
      let statusInt;
      if (status !== null) {
        statusInt = parseInt(status);
      } else {
        statusInt = alignment.includes("Pending") ? 0 : 1;
      }

      setIsClosed(statusInt === 1);

      try {
        let response;
        const cleanSearchValue = getCleanSearchValue(search);

        if (role_id === 1) {
          const payload = {
            page: targetPage,
            limit: 10,
          };

          const filter = {
            filter: {
              status: statusInt.toString(),
              search: cleanSearchValue,
              ...optionalfilter,
            },
          };

          response = await bmrSubjectApi.requestHistory(
            payload.limit,
            payload.page,
            payload.phase_id,
            payload.subject_id,
            filter
          );
        } else {
          const filter = {
            status: statusInt,
            search: cleanSearchValue,
            sort_by: null,
            ...optionalfilter,
          };

          response = await bmrSubjectApi.tasklist(10, targetPage, filter);
        }

        // Check if request was aborted
        if (abortControllerRef.current?.signal?.aborted) {
          return;
        }

        if (response?.stats) {
          setTaskCount(response);
        } else {
          setTaskCount({});
        }

        if (response?.data?.length) {
          setTaskData((prev) => {
            if (isLoadMore) {
              // Avoid duplicates by checking if items already exist
              const existingIds = new Set(prev.map((item) => item.id));
              const newItems = response.data.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...newItems];
            } else {
              return response.data;
            }
          });

          // Check if we have more data
          const hasMore = response.data.length >= 10;
          setHasMoreData(hasMore);
        } else {
          if (!isLoadMore) {
            setTaskData([]);
          }
          setHasMoreData(false);
        }

        if (response?.page_details) {
          const calculatedMaxPages = Math.ceil(
            response.page_details.no_of_records / response.page_details.limit
          );
          setMaxPages(calculatedMaxPages);
          setHasMoreData(targetPage < calculatedMaxPages);
        }
        setLoading(false);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        console.error("API Error:", error);
        notify.error(error?.message || error || "Failed to fetch data")
        
        setHasMoreData(false);
        setLoading(false);
      } finally {
        isLoadingMore.current = false;
        abortControllerRef.current = null;
      }
    },
    [alignment, search, dispatch, role_id] // Removed filters from dependencies
  );

  const handleFilterSubmit = (p, v) => {
    if (!p) p = {};

    let sendOptionalParameter = {};
    Object.keys(p).forEach((key) => {
      if (p[key] !== null && p[key] !== undefined && p[key] !== "") {
        sendOptionalParameter[key] = p[key];
      }
    });
    if (v?.from) sendOptionalParameter.startDate = v.from;
    if (v?.to) sendOptionalParameter.endDate = v.to;

    setFilters(sendOptionalParameter);
    setPage(1);
    setHasMoreData(true);

    // Trigger data fetch with new filters
    const status = alignment.includes("Pending") ? 0 : 1;
    handleGetHistoryData(status, sendOptionalParameter, false, 1);
  };

  const handlePhaseSearch = (value) => {
    setSearch(value);
    setPage(1);
    setHasMoreData(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleExportPendingTask = async () => {
    try {
      const res = await bmrSubjectApi.exportRequestListById(
        null,
        alignment.includes("Pending") ? 0 : 1
      );
      if (!res) {
        notify.error(res?.message)
      } else {
        const blob = new Blob([res], { type: "text/plain" });
        const fileURL = URL.createObjectURL(blob);
        const downloadLink = document.createElement("a");
        downloadLink.href = fileURL;
        downloadLink.download = "request_reason.csv";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        URL.revokeObjectURL(fileURL);
      }
    } catch (error) {
      notify.error(error)
    }
  };

  const handlePendingFilter = () => {
    getAllPhaseDropDown();
    setOpenFilter(true);
  };

  const getStatusDropDown = async () => {
    try {
      const res = await bmrSubjectApi.getAllRequest();
      const result = res.data.map((item) => ({
        id: item.id,
        name: item.title,
      }));
      setRequestData(result);
    } catch (error) {
      notify.error(error)
    }
  };

  const getAllPhaseDropDown = async () => {
    try {
      const res = await bmrEditorApi.getPhaseDropDown(login_id);
      const result = res.data.map((item) => ({
        id: item.id,
        name: `${item.title}${
          item.product_details?.name ? ` (${item.product_details.name})` : ""
        }`,
      }));
      const productResult = res.data
        .filter((item) => item.product_details)
        .map((item) => ({
          id: item.product_details.id,
          name: item.product_details.name,
        }));
      const uniqueProductData = Array.from(
        new Map(productResult.map((item) => [item.id, item])).values()
      ).sort((a, b) => a.name.localeCompare(b.name));
      setProductData(uniqueProductData);
      setPhaseData(result);
    } catch (error) {
      notify.error(error)
    }
  };

  // FIXED: Improved loadMoreData function
  const loadMoreData = useCallback(() => {
    if (!hasMoreData || isLoadingMore.current || loading) {
      return;
    }

    if (maxPages && page >= maxPages) {
      setHasMoreData(false);
      return;
    }

    const nextPage = page + 1;
    setPage(nextPage);

    // Call API with the next page
    const status = alignment.includes("Pending") ? 0 : 1;
    handleGetHistoryData(status, filters, true, nextPage);
  }, [
    hasMoreData,
    loading,
    maxPages,
    page,
    alignment,
    filters,
    handleGetHistoryData,
  ]);

  // FIXED: Separate useEffect for search changes with debouncing
  useEffect(() => {
    if (isValidSearchInput(search)) {
      const timeoutId = setTimeout(() => {
        const status = alignment.includes("Pending") ? 0 : 1;
        setPage(1);
        handleGetHistoryData(status, filters, false, 1);
      }, 300); // 300ms debounce
      return () => clearTimeout(timeoutId);
    }
  }, [search, filters,alignment]); // Trigger on search and filters change

  // Initial data loading
  useEffect(() => {
    getStatusDropDown();
    getAllPhaseDropDown();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleNavigate = () => {
     navigate(-1);
  };

 const handleNavigate2 = () => {
  const routes = {
    "quality-check": "/bmr/lsm",
    "logistics-management": "/bmr/logistics-management/in-bound",
    "warehouse": "/bmr/logistics-management/in-bound",
    "manufacture-lead": "/bmr/manufacturer",
    "qc-lead": "/bmr/qc",
  };
  navigate(routes?.[role_slug] || "/bmr/feasibility-assessment/subjects");
};

  // Conditional rendering function for the tile component
  const renderTaskTile = () => {
    return role_id === 1 ? (
      <PendingTaskTile
        task={taskData}
        isClosed={isClosed}
        loadMoreData={loadMoreData}
        page={page}
        maxPages={maxPages}
        loading={loading}
        alignment={alignment}
        hasMoreData={hasMoreData}
      />
    ) : (
      <TaskNotificationTile
        task={taskData}
        isClosed={isClosed}
        loadMoreData={loadMoreData}
        page={page}
        maxPages={maxPages}
        loading={loading}
        alignment={alignment}
        hasMoreData={hasMoreData}
      />
    );
  };

  return (
    <div>
      <div className="flex flex-col gap-4 mt-4 ml-4 mr-4">
        <div
        className="cursor-pointer flex items-center mb-0 gap-1 w-[fit-content]"
        onClick={singleModuleAccess ? handleNavigate2 : handleNavigate}
      >
          <div className="block p-2 !pl-0 w-max">
            <BackNavIcon width={18} height={18} />
          </div>
          <span className="!m-0">Back</span>
      </div>

        <PendingTaskHeader
          SearchbarPlaceholder="Search by Product/Phase/Patient name"
          width={"100%"}
          handleButtonClick={handlePendingFilter}
          handleSearch={handlePhaseSearch}
          showExportButton={role_id === 1}
          handleExportButton={handleExportPendingTask}
          taskCount={taskCount}
          openFilter={openFilter}
          closeFilter={handleCloseFilter}
          setOpenFilter={setOpenFilter}
          handleFilterSubmit={handleFilterSubmit}
          handleGetHistoryData={handleGetHistoryData}
          setAlignment={setAlignment}
          alignment={alignment}
          requestData={requestData}
          phaseData={phaseData}
          productData={productData}
          initialVal={PendingTasks}
          page={page}
          setPage={setPage}
          setFilters={setFilters}
        />
      </div>
      <div className="px-4 pt-3">{renderTaskTile()}</div>
    </div>
  );
};

export default PendingTask;
